import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Home, X, CheckCircle } from 'lucide-react';
import AnswerCard from './components/AnswerCard';
import QuestionRenderer from './components/QuestionRenderer';
import { supabase } from '../../supabase/client';
import type { Question, Paper } from './types';
import { typeNames, subTypeNames } from '../../constants/questionTypes';
import { categoryStore } from '../../store/categoryStore';
import { getImageUrl } from '../../utils/questionHtml';
import QuestionHtmlWithZoom from '../../components/QuestionHtmlWithZoom';
import PreviewControls from './components/PreviewControls';

interface QuizPreviewModeProps {
  isEditorPreview?: boolean;
  previewData?: any;
  onBack: () => void;
}

export default function QuizPreviewMode({
  isEditorPreview = false,
  previewData,
  onBack
}: QuizPreviewModeProps) {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();

  const [paper, setPaper] = useState<Paper | null>(null);
  const [examQuestions, setExamQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [markedQuestions] = useState<Set<number>>(new Set());
  const [showSidebar, setShowSidebar] = useState(false);
  const [hasWidthChanges, setHasWidthChanges] = useState(false);
  const [isSavingWidths, setIsSavingWidths] = useState(false);
  const [initialWidths, setInitialWidths] = useState<Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidth?: number }[] }>>({});
  const [loading, setLoading] = useState(true);

  const currentQuestion = examQuestions[currentIndex];
  const isSubmitted = false;
  const aiExplanations: Record<string, string> = {};

  // 加载预览数据
  useEffect(() => {
    const loadPreviewData = async () => {
      setLoading(true);

      // 优先从 previewData 获取（PaperEditor 跳转）
      if (previewData) {
        setPaper({
          id: previewData.paperId,
          title: previewData.paperTitle,
          questions: previewData.questions,
          total_score: previewData.totalScore,
          category_type: ''
        });
        setExamQuestions(previewData.questions);
        // 记录初始缩放快照
        const snapshot: Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidth?: number }[] }> = {};
        previewData.questions.forEach((q: Question) => {
          snapshot[q.id] = {
            titleImageWidths: q.titleImageWidths ? [...q.titleImageWidths] : undefined,
            optionImageWidths: q.options?.map(o => ({ id: o.id, imageWidth: o.imageWidth ?? 100 }))
          };
        });
        setInitialWidths(snapshot);
        setHasWidthChanges(false);
        setLoading(false);
        return;
      }

      // 从数据库加载（试卷列表/草稿列表跳转）
      if (paperId) {
        const { data: paperData } = await supabase.from('papers').select('*').eq('id', paperId).single();
        if (paperData) {
          const { data: questionsData } = await supabase.from('questions').select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
          const mappedQuestions = (questionsData || []).map((q: any) => {
            const optionImageWidths = q.option_image_widths || {};
            const mappedOptions = (q.options || []).map((opt: any) => ({
              ...opt,
              imageWidth: optionImageWidths[opt.id] || opt.imageWidth || 100
            }));
            return {
              id: q.id,
              type: q.question_type || 'single_choice',
              subType: q.scratch_subtype,
              name: q.name || '',
              title: q.content,
              options: mappedOptions,
              score: q.score || 2,
              explanation: q.explanation,
              titleImages: q.title_images || [],
              titleImageWidths: q.title_image_widths || [],
              imagePosition: q.image_position || 'below',
              titleImageLayout: q.title_image_layout || 'auto',
              optionLayout: q.option_layout || 'grid'
            };
          });
          setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
          setExamQuestions(mappedQuestions);
          // 记录初始缩放快照
          const snapshot: Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidth?: number }[] }> = {};
          mappedQuestions.forEach((q: Question) => {
            snapshot[q.id] = {
              titleImageWidths: q.titleImageWidths ? [...q.titleImageWidths] : undefined,
              optionImageWidths: q.options?.map(o => ({ id: o.id, imageWidth: o.imageWidth ?? 100 }))
            };
          });
          setInitialWidths(snapshot);
          setHasWidthChanges(false);
        }
      }
      setLoading(false);
    };

    loadPreviewData();
  }, [paperId, previewData]);

  const getQuestionTypeName = (type: string, subType?: string) => {
    if (type === 'scratch_coding') return categoryStore.getCategoryNameByCode('scratch') || '图形化编程';
    if (type === 'coding' && subType) return subTypeNames[subType] || '主观题';
    return typeNames[type] || '单选题';
  };

  const handleAnswerChange = () => {
    // 预览模式下不保存答案
  };

  const handleTextAnswerChange = () => {
    // 预览模式下不保存答案
  };

  const getAIGrading = async () => {
    // 预览模式下不执行AI评分
  };

  // 保存缩放设置到数据库
  const handleSaveWidthSettings = async () => {
    if (isSavingWidths || !paperId) return;
    setIsSavingWidths(true);
    try {
      for (const q of examQuestions) {
        await supabase.from('questions').update({
          title_image_widths: q.titleImageWidths || [],
          title_image_layout: (q as any).titleImageLayout || 'auto',
          image_position: (q as any).imagePosition || 'below',
          option_image_widths: q.options?.reduce((acc: Record<string, number>, o: any) => {
            if (o.imageWidth) acc[o.id] = o.imageWidth;
            return acc;
          }, {}) || {},
          options: q.options || [],
          option_layout: (q as any).optionLayout || 'grid'
        }).eq('id', q.id);
      }
      showToast('保存成功', 'success');
      // 保存成功后更新初始快照
      const snapshot: Record<string, { titleImageWidths?: number[]; optionImageWidths?: { id: string; imageWidth?: number }[] }> = {};
      examQuestions.forEach((q: Question) => {
        snapshot[q.id] = {
          titleImageWidths: q.titleImageWidths ? [...q.titleImageWidths] : undefined,
          optionImageWidths: q.options?.map(o => ({ id: o.id, imageWidth: o.imageWidth ?? 100 }))
        };
      });
      setInitialWidths(snapshot);
      setHasWidthChanges(false);
    } catch (error) {
      showToast('保存失败，请重试', 'error');
    } finally {
      setIsSavingWidths(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-all duration-300 ${type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!paper || examQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">试卷加载失败</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 lg:h-24 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <button onClick={() => {
              if (isEditorPreview) {
                const widthUpdates = examQuestions.map(q => ({
                  id: q.id,
                  titleImageWidths: q.titleImageWidths,
                  optionImageWidths: q.options?.map(o => ({ id: o.id, imageWidth: o.imageWidth }))
                }));
                localStorage.setItem('previewImageWidthUpdates', JSON.stringify(widthUpdates));
              }
              onBack();
            }} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-xs lg:text-sm">返回</span>
            </button>
            <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <Home className="w-4 h-4" />
              <span className="text-xs lg:text-sm hidden sm:inline">首页</span>
            </button>
          </div>
          <div className="flex flex-col items-center min-w-0 px-2">
            <h1 className="font-bold text-gray-900 text-sm sm:text-base lg:text-2xl truncate max-w-[200px] sm:max-w-[400px] lg:max-w-none">
              {paper?.title?.split('图形化')[0] || '试卷预览'}
            </h1>
            {paper?.title?.includes('图形化') && <p className="text-xs lg:text-xl text-gray-500 truncate">图形化{paper.title.split('图形化')[1]}</p>}
          </div>
          <div className="flex items-center gap-2 lg:gap-4 shrink-0">
            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">预览模式</span>
            {isEditorPreview && (
              <button
                onClick={handleSaveWidthSettings}
                disabled={!hasWidthChanges || isSavingWidths}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                  isSavingWidths
                    ? 'bg-emerald-400 text-white cursor-wait'
                    : hasWidthChanges
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSavingWidths ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    保存中...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    保存试卷
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
        {/* Mobile Answer Card Button */}
        <button onClick={() => setShowSidebar(true)} className="lg:hidden fixed bottom-4 right-4 z-40 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Sidebar - Answer Card */}
          <div className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
            <AnswerCard
              examQuestions={examQuestions}
              currentIndex={currentIndex}
              answers={answers}
              markedQuestions={markedQuestions}
              isSubmitted={isSubmitted}
              paper={paper}
              onQuestionSelect={setCurrentIndex}
            />
          </div>

          {/* Question Area */}
          <div className="lg:col-span-3 space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Question Header */}
              <div className="sticky lg:top-24 z-30 bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 lg:p-6 border-b border-blue-200">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 lg:gap-3 shrink-0">
                    <span className="px-2 py-0.5 lg:px-3 lg:py-1 bg-blue-600 text-white text-xs lg:text-sm font-medium rounded-full">
                      {getQuestionTypeName(currentQuestion?.type, currentQuestion?.subType)}
                    </span>
                  </div>
                  <div className="hidden sm:flex items-center gap-2">
                    <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                      <ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4" />上一题
                    </button>
                    <button onClick={() => setCurrentIndex(Math.min(examQuestions.length - 1, currentIndex + 1))} disabled={currentIndex === examQuestions.length - 1} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all">
                      下一题<ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4 rotate-180" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 lg:gap-2 ml-auto">
                    <PreviewControls
                      question={currentQuestion}
                      examQuestions={examQuestions}
                      onExamQuestionsChange={setExamQuestions}
                      onHasWidthChangesChange={setHasWidthChanges}
                    />
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-100">
                <div className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold flex-shrink-0">{currentIndex + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-3 relative">
                        {(() => {
                          const imagePosition = (currentQuestion as any).imagePosition || 'below';
                          const currentLayout = (currentQuestion as any)?.titleImageLayout || 'auto';
                          const hasTitleImages = currentQuestion?.titleImages && currentQuestion.titleImages.length > 0 && !currentQuestion?.title?.includes('<img');

                          // 判断是否为编程题（非客观题），使用图文混排模式
                          const isCodingQuestion = currentQuestion?.type === 'python_coding' ||
                                                   currentQuestion?.type === 'cpp_coding' ||
                                                   currentQuestion?.type === 'scratch_coding';

                          // 非客观题显示题目名称（如果有）- 使用 name 字段（题目名称，管理用）
                          // 如果 name 为空，按编程题序号生成"未命名1"、"未命名2"等
                          const questionName = isCodingQuestion
                            ? (currentQuestion?.name || (() => {
                                const codingQuestions = examQuestions.filter(q =>
                                  q.type === 'python_coding' || q.type === 'cpp_coding' || q.type === 'scratch_coding'
                                );
                                const codingIndex = codingQuestions.findIndex(q => q.id === currentQuestion?.id) + 1;
                                return `未命名${codingIndex}`;
                              })())
                            : null;

                          const titleText = currentQuestion?.title?.includes('<img') || currentQuestion?.title?.startsWith('<') ? (
                            <div className="prose prose-sm max-w-none">
                              <QuestionHtmlWithZoom
                                html={currentQuestion.title}
                                imageWidths={currentQuestion.titleImageWidths}
                                imageLayout={isCodingQuestion ? 'auto' : ((currentQuestion as any)?.titleImageLayout || 'auto')}
                                onWidthChange={(newWidths) => {
                                  setHasWidthChanges(true);
                                  setExamQuestions(examQuestions.map(q =>
                                    q.id === currentQuestion.id ? { ...q, titleImageWidths: newWidths } : q
                                  ));
                                }}
                              />
                            </div>
                          ) : (
                            <span>{currentQuestion?.title}</span>
                          );

                          const titleImagesEl = hasTitleImages ? (
                            <div className={`grid gap-3 ${
                              currentLayout === 'cols1' ? 'grid-cols-1' :
                              currentLayout === 'cols2' ? 'grid-cols-2' :
                              currentLayout === 'cols3' ? 'grid-cols-3' :
                              currentLayout === 'cols4' ? 'grid-cols-4' :
                              'grid-cols-1'
                            }`}>
                              {currentQuestion.titleImages?.map((imgUrl, idx) => {
                                const fullUrl = getImageUrl(imgUrl);
                                const savedWidth = currentQuestion.titleImageWidths?.[idx];
                                const displayWidth = savedWidth != null ? savedWidth : 100;
                                return (
                                  <div key={idx} className="relative overflow-hidden">
                                    <img
                                      src={fullUrl}
                                      alt={`题目图片 ${idx + 1}`}
                                      className="rounded-lg border border-gray-200"
                                      style={{ width: '100%', maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                                      loading="lazy"
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                    <div className="flex items-center gap-1 mt-1">
                                      <span className="text-xs text-gray-500">缩放:</span>
                                      <input
                                        type="range"
                                        min="10"
                                        max="100"
                                        step="1"
                                        value={displayWidth}
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value);
                                          const newWidths = [...(currentQuestion.titleImageWidths || currentQuestion.titleImages?.map(() => 100) || [])];
                                          newWidths[idx] = val;
                                          setHasWidthChanges(true);
                                          setExamQuestions(examQuestions.map(q =>
                                            q.id === currentQuestion.id ? { ...q, titleImageWidths: newWidths } : q
                                          ));
                                        }}
                                        className="w-20 h-1 accent-blue-500 cursor-pointer"
                                      />
                                      <input
                                        type="number"
                                        min="10"
                                        max="100"
                                        value={displayWidth}
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value);
                                          if (!isNaN(val) && val >= 10 && val <= 100) {
                                            const newWidths = [...(currentQuestion.titleImageWidths || currentQuestion.titleImages?.map(() => 100) || [])];
                                            newWidths[idx] = val;
                                            setHasWidthChanges(true);
                                            setExamQuestions(examQuestions.map(q =>
                                              q.id === currentQuestion.id ? { ...q, titleImageWidths: newWidths } : q
                                            ));
                                          }
                                        }}
                                        className="w-16 h-5 text-sm text-center border border-gray-300 rounded focus:border-blue-500 outline-none"
                                      />
                                      <span className="text-xs text-gray-400">%</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null;

                          return (
                            <>
                              {/* 非客观题显示题目名称 */}
                              {questionName && (
                                <div className="mb-3 pb-3 border-b border-gray-200">
                                  <span className="text-sm font-medium text-gray-500">题目名称：</span>
                                  <span className="text-base font-semibold text-gray-800">{questionName}</span>
                                </div>
                              )}
                              {imagePosition === 'above' && titleImagesEl}
                              {titleText}
                              {imagePosition === 'below' && titleImagesEl}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Answer Area - Preview Mode */}
              <div className="p-3 sm:p-4 lg:p-6">
                <QuestionRenderer
                  key={currentQuestion?.id}
                  question={currentQuestion}
                  currentIndex={currentIndex}
                  answers={answers}
                  isSubmitted={false}
                  isPreview={true}
                  paperId={paperId}
                  currentUser={null}
                  aiExplanations={aiExplanations}
                  examQuestions={examQuestions}
                  aiGradingEnabled={true}
                  isAssignmentMode={false}
                  onAnswerChange={handleAnswerChange}
                  onTextAnswerChange={handleTextAnswerChange}
                  onAIGrading={getAIGrading}
                  onExamQuestionsChange={setExamQuestions}
                  onHasWidthChangesChange={setHasWidthChanges}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.3 }} className="fixed inset-y-0 right-0 w-[85vw] sm:w-80 bg-white shadow-xl z-50 lg:hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">答题卡</h2>
                <button onClick={() => setShowSidebar(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-600" /></button>
              </div>
              <div className="p-4 overflow-y-auto">
                <AnswerCard
                  examQuestions={examQuestions}
                  currentIndex={currentIndex}
                  answers={answers}
                  markedQuestions={markedQuestions}
                  isSubmitted={isSubmitted}
                  paper={paper}
                  onQuestionSelect={setCurrentIndex}
                  onCloseSidebar={() => setShowSidebar(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
